import { useRef } from 'react';
import clsx from 'clsx';
import {
  mergeProps,
  useCheckbox,
  useFocusRing,
  VisuallyHidden,
} from 'react-aria';
import { useToggleState } from 'react-stately';
import { CheckboxOff, CheckboxOn } from '@hungryroot/icons';
import { Text } from '../typography/Text/Text';
import styles from './Checkbox.module.css';

interface Props {
  testid?: string;
  label: string | JSX.Element;
  value: string | number;
  isChecked?: boolean;
  disabled?: boolean;
  className?: string;
  onChange: (value: boolean) => void;
  checkboxClassName?: string;
}

export function Checkbox(props: Props) {
  const {
    testid,
    label,
    value,
    isChecked,
    className,
    onChange,
    disabled,
    checkboxClassName,
  } = props;

  const toggleState = useToggleState({
    isDisabled: disabled,
    isSelected: isChecked,
    value: value.toString(),
    onChange: (isSelected) => {
      onChange(isSelected);
    },
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const { inputProps } = useCheckbox(
    {
      children: label,
      isDisabled: disabled,
      isSelected: isChecked,
      value: value.toString(),
    },
    toggleState,
    inputRef
  );

  const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <span data-testid={testid}>
      <label
        className={clsx(styles.label, className)}
        data-testid={`${testid}-label`}
      >
        <div
          className={clsx(
            styles['checkbox-container'],
            !isChecked ? styles.off : styles.on,
            isFocusVisible && styles.focused,
            checkboxClassName
          )}
          data-testid={`${testid}-container`}
        >
          <VisuallyHidden>
            <input
              {...mergeProps(inputProps, focusProps)}
              ref={inputRef}
              data-testid={`${testid}-input`}
            />
          </VisuallyHidden>
          {isChecked ? (
            <CheckboxOn data-testid={`${testid}-icon`} />
          ) : (
            <CheckboxOff data-testid={`${testid}-icon`} />
          )}
        </div>
        <Text className={styles['label-text']} testid={`${testid}-label-text`}>
          {label}
        </Text>
      </label>
    </span>
  );
}
